import type { CommissionRule, LaneCard, ProductTypeId } from 'app/pages/CommissionManager/types';
import type { ProductTypeCondition, AccommodationCondition } from 'app/pages/CommissionManager/types';
import {
  ProductTypeIdApi,
  SelectionModeApi,
  type CommissionGroupDto,
  type ProductSelectionDto,
} from './types';

const PRODUCT_TYPE_MAP: Record<ProductTypeId, number> = {
  Course: ProductTypeIdApi.Course,
  Accommodation: ProductTypeIdApi.Accommodation,
  AddOn: ProductTypeIdApi.AddOn,
  Transfer: ProductTypeIdApi.Transfer,
  Fees: ProductTypeIdApi.Fee,
  Package: ProductTypeIdApi.Package,
};

const PRODUCT_TYPE_REVERSE: Record<number, ProductTypeId> = {
  [ProductTypeIdApi.Course]: 'Course',
  [ProductTypeIdApi.Accommodation]: 'Accommodation',
  [ProductTypeIdApi.AddOn]: 'AddOn',
  [ProductTypeIdApi.Transfer]: 'Transfer',
  [ProductTypeIdApi.Fee]: 'Fees',
  [ProductTypeIdApi.Package]: 'Package',
};

function laneCardToProductSelection(card: LaneCard, isExcluded: boolean): ProductSelectionDto | null {
  const productTypeId = PRODUCT_TYPE_MAP[card.productType];
  const cond = card.condition;

  const selection: ProductSelectionDto = {
    productTypeId,
    accommodationTypeIds: [],
    addOnTypeIds: [],
    productIds: [],
    selectionMode: isExcluded ? SelectionModeApi.ExcludeSelected : SelectionModeApi.SpecificProductType,
    minDuration: 0,
    maxDuration: 0,
  };

  if (!cond || cond.mode === 'ANY') {
    selection.selectionMode = isExcluded ? SelectionModeApi.NoSelectionMode : SelectionModeApi.SpecificProductType;
    selection.productIds = [];
    selection.accommodationTypeIds = [];
    selection.addOnTypeIds = [];
    return selection;
  }

  if (cond.mode === 'CHOOSE_TYPE' && 'typeId' in cond && cond.typeId != null && Number(cond.typeId) > 0) {
    if (card.productType === 'AddOn') {
      selection.selectionMode = isExcluded ? SelectionModeApi.ExcludeSelected : SelectionModeApi.IncludeSelected;
      selection.addOnTypeIds = [Number(cond.typeId)];
      selection.accommodationTypeIds = [];
      selection.productIds = [];
      return selection;
    }
    if (card.productType === 'Accommodation') {
      selection.selectionMode = isExcluded ? SelectionModeApi.ExcludeSelected : SelectionModeApi.IncludeSelected;
      selection.accommodationTypeIds = [Number(cond.typeId)];
      selection.productIds = [];
      selection.addOnTypeIds = [];
      return selection;
    }
  }

  if (cond.mode === 'SELECT' && 'selectedIds' in cond && cond.selectedIds?.length) {
    selection.selectionMode = isExcluded ? SelectionModeApi.ExcludeSelected : SelectionModeApi.IncludeSelected;
    selection.productIds = cond.selectedIds;
    selection.accommodationTypeIds = [];
    selection.addOnTypeIds = [];
    return selection;
  }

  selection.selectionMode = isExcluded ? SelectionModeApi.NoSelectionMode : SelectionModeApi.SpecificProductType;
  selection.productIds = [];
  selection.accommodationTypeIds = [];
  selection.addOnTypeIds = [];
  return selection;
}

function isValidForApi(ps: ProductSelectionDto): boolean {
  if (ps.selectionMode !== SelectionModeApi.IncludeSelected && ps.selectionMode !== SelectionModeApi.ExcludeSelected) {
    return true;
  }
  const hasIds =
    (ps.productIds?.length ?? 0) > 0 ||
    (ps.accommodationTypeIds?.length ?? 0) > 0 ||
    (ps.addOnTypeIds?.length ?? 0) > 0;
  return hasIds;
}

export function rulesToCommissionGroupsDto(
  rules: CommissionRule[],
  divisionId: number
): CommissionGroupDto[] {
  return rules.map((rule) => {
    const productSelections: ProductSelectionDto[] = [];
    for (const card of rule.include) {
      const ps = laneCardToProductSelection(card, false);
      if (ps && isValidForApi(ps)) productSelections.push(ps);
    }
    for (const card of rule.exclude) {
      const ps = laneCardToProductSelection(card, true);
      if (ps && isValidForApi(ps)) productSelections.push(ps);
    }
    const numericId = rule.id.startsWith('rule-') ? 0 : Number(rule.id);
    return {
      id: numericId || 0,
      name: rule.name || '',
      productSelections,
      feeIds: rule.feesIncludeAny ? [] : rule.fees.map((f) => f.id),
      active: rule.active !== false,
    };
  });
}

function resolveNames(
  ids: number[],
  productTypeId: number,
  productNames?: ProductNameLookup
): string[] {
  if (!productNames || !ids.length) return ids.map(String);
  let map: Map<number, string> | undefined;
  if (productTypeId === ProductTypeIdApi.Course) map = productNames.courses;
  else if (productTypeId === ProductTypeIdApi.Accommodation) map = productNames.accommodations;
  else if (productTypeId === ProductTypeIdApi.AddOn) map = productNames.addons;
  else if (productTypeId === ProductTypeIdApi.Transfer) map = productNames.transfers;
  else if (productTypeId === ProductTypeIdApi.Fee) map = productNames.fees;
  else if (productTypeId === ProductTypeIdApi.Package) map = productNames.packages;
  return ids.map((id) => map?.get(id) ?? String(id));
}

function lookupTypeLabel(map: Map<number, string> | undefined, rawId: number): string | undefined {
  if (!map?.size) return undefined;
  const id = Number(rawId);
  if (!Number.isFinite(id) || id <= 0) return undefined;
  const direct = map.get(id);
  if (direct != null && direct !== '') return direct;
  for (const [k, v] of map) {
    if (Number(k) === id && v != null && v !== '') return v;
  }
  return undefined;
}

function productSelectionToLaneCard(
  ps: ProductSelectionDto,
  index: number,
  productNames?: ProductNameLookup
): LaneCard {
  const productType = PRODUCT_TYPE_REVERSE[ps.productTypeId] ?? 'Course';
  let condition: ProductTypeCondition | AccommodationCondition;
  let summary: string;

  const isSelectMode = ps.selectionMode === SelectionModeApi.IncludeSelected || ps.selectionMode === SelectionModeApi.ExcludeSelected;
  if (
    ps.selectionMode === SelectionModeApi.SpecificProductType ||
    ps.selectionMode === SelectionModeApi.IncludeAll ||
    ps.selectionMode === SelectionModeApi.NoSelectionMode
  ) {
    condition = { mode: 'ANY', selectedIds: [] };
    summary = 'Any';
  } else if (isSelectMode && productType === 'Accommodation' && ps.accommodationTypeIds?.length) {
    const typeId = Number(ps.accommodationTypeIds[0]);
    const typeName = lookupTypeLabel(productNames?.accommodationTypes, typeId);
    condition = {
      mode: 'CHOOSE_TYPE',
      typeId,
      selectedIds: [],
      ...(typeName ? { typeName } : {}),
    };
    summary = typeName ?? 'Specify product';
  } else if (
    isSelectMode &&
    productType === 'AddOn' &&
    (ps.addOnTypeIds?.length ?? 0) > 0 &&
    !(ps.productIds?.length ?? 0)
  ) {
    const typeId = Number(ps.addOnTypeIds![0]);
    const typeName = lookupTypeLabel(productNames?.addOnTypes, typeId);
    condition = {
      mode: 'CHOOSE_TYPE',
      typeId,
      selectedIds: [],
      typeName,
    };
    summary = typeName ?? 'Specify product';
  } else if (isSelectMode && (ps.productIds?.length || ps.addOnTypeIds?.length)) {
    const ids = ps.productIds?.length ? ps.productIds : ps.addOnTypeIds ?? [];
    const names = resolveNames(ids, ps.productTypeId, productNames);
    condition = { mode: 'SELECT', selectedIds: ids, selectedNames: names };
    summary = names.join(', ');
  } else {
    condition = { mode: 'ANY', selectedIds: [] };
    summary = 'Specify product';
  }

  return {
    id: `card-${index}-${Date.now()}`,
    productType,
    condition,
    summary,
  };
}

export type ProductNameLookup = {
  courses?: Map<number, string>;
  accommodations?: Map<number, string>;
  addons?: Map<number, string>;
  transfers?: Map<number, string>;
  accommodationTypes?: Map<number, string>;
  addOnTypes?: Map<number, string>;
  fees?: Map<number, string>;
  packages?: Map<number, string>;
};

export function commissionGroupDtoToRule(
  dto: CommissionGroupDto,
  feeNames?: Map<number, string>,
  productNames?: ProductNameLookup
): CommissionRule {
  const selections = dto.productSelections || [];
  const include: LaneCard[] = [];
  const exclude: LaneCard[] = [];
  selections.forEach((ps, i) => {
    const card = productSelectionToLaneCard(ps, i, productNames);
    if (ps.selectionMode === SelectionModeApi.ExcludeSelected || ps.selectionMode === SelectionModeApi.NoSelectionMode) {
      exclude.push(card);
    } else {
      include.push(card);
    }
  });
  const feeIds = dto.feeIds || [];
  return {
    id: dto.id ? String(dto.id) : `rule-${Date.now()}`,
    name: dto.name || '',
    include,
    exclude,
    feesIncludeAny: feeIds.length === 0,
    fees: feeIds.map((id) => ({ id, name: feeNames?.get(id) ?? '' })),
    active: dto.active !== false,
  };
}